/**
 * QuestionPerformance Component
 *
 * Analytics component for detailed question performance metrics including
 * usage counts, correct/incorrect answers, and average grades.
 *
 * @module QuestionPerformance
 */

import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  BarChart2,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Filter,
  Eye,
  Loader2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import QuestionPreview from '@/components/admin/QuestionPreview';
import { getQuestionById } from '@/lib/questionApi';
import { getQuestionPerformance } from '@/lib/analyticsApi';
import { toast } from 'sonner';
import { CEFR_LEVELS, MODULES } from '@/lib/constants';
import { logger } from '@/lib/logger';

interface QuestionPerformanceData {
  id: string;
  question_text: string;
  level: string;
  module: string;
  exam_type: string[];
  section: number;
  usage_count: string;
  correct_count: string;
  incorrect_count: string;
  unanswered_count: string;
  avg_grade: string;
}

// Helper function to get module display name
const getModuleDisplayName = (module: string): string => {
  const moduleMap: Record<string, string> = {
    reading: 'Reading',
    writing: 'Writing',
    listening: 'Listening',
    speaking: 'Speaking',
  };
  return moduleMap[module.toLowerCase()] || module;
};

export default function QuestionPerformance() {
  const [data, setData] = useState<QuestionPerformanceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('usage_count');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Independent filter state
  const [examType, setExamType] = useState('all');
  const [level, setLevel] = useState('all');
  const [module, setModule] = useState('all');
  const [section, setSection] = useState('all');

  // Preview state
  const [previewQuestion, setPreviewQuestion] = useState<any | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page,
        limit: 10,
        sortBy,
        sortOrder,
      };

      if (examType !== 'all') params.examType = examType;
      if (level !== 'all') params.level = level;
      if (module !== 'all') params.module = module;
      if (section !== 'all') params.section = section;

      const result = await getQuestionPerformance(params);
      setData(result.data || []);
      setTotalPages(result.pagination?.totalPages || 1);
    } catch (error) {
      logger.error('Error fetching question performance:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPerformanceData();
  }, [page, sortBy, sortOrder, examType, level, module, section]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handlePreview = async (id: string) => {
    try {
      setPreviewLoading(true);
      const question = await getQuestionById(id);
      setPreviewQuestion(question);
    } catch (error) {
      logger.error('Error fetching question details:', error);
      toast.error('Failed to load question details');
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatPercentage = (count: string, total: string) => {
    const c = Number.parseInt(count, 10);
    const t = Number.parseInt(total, 10);
    if (t === 0) return '0%';
    return `${Math.round((c / t) * 100)}%`;
  };

  return (
    <div className="space-y-4">
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-ui">
            <BarChart2 className="h-5 w-5" />
            Question Performance Analysis
          </CardTitle>
          <CardDescription className="font-ui">
            Detailed performance metrics for individual questions including usage,
            correctness rates, and average grades
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-muted/30 rounded-lg border">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mr-2 font-ui">
              <Filter className="h-4 w-4" />
              <span>Filters:</span>
            </div>

            <Select value={examType} onValueChange={setExamType}>
              <SelectTrigger className="w-[140px] bg-background">
                <SelectValue placeholder="Exam Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="font-ui">All Types</span>
                </SelectItem>
                <SelectItem value="Goethe">
                  <span className="font-ui">Goethe</span>
                </SelectItem>
                <SelectItem value="OSD">
                  <span className="font-ui">OSD</span>
                </SelectItem>
                <SelectItem value="Telc">
                  <span className="font-ui">Telc</span>
                </SelectItem>
              </SelectContent>
            </Select>

            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="w-[120px] bg-background">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="font-ui">All Levels</span>
                </SelectItem>
                {CEFR_LEVELS.map((lvl) => (
                  <SelectItem key={lvl} value={lvl}>
                    <span className="font-ui">{lvl}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={module} onValueChange={setModule}>
              <SelectTrigger className="w-[130px] bg-background">
                <SelectValue placeholder="Module" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="font-ui">All Modules</span>
                </SelectItem>
                {MODULES.map((mod) => (
                  <SelectItem key={mod.value} value={mod.value}>
                    <span className="font-ui">
                      {getModuleDisplayName(mod.value)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={section} onValueChange={setSection}>
              <SelectTrigger className="w-[130px] bg-background">
                <SelectValue placeholder="Section" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="font-ui">All Sections</span>
                </SelectItem>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <SelectItem key={num} value={String(num)}>
                    <span className="font-ui">Section {num}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[300px] font-ui">Question</TableHead>
                  <TableHead className="font-ui">Details</TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50 font-ui"
                    onClick={() => handleSort('usage_count')}
                  >
                    <div className="flex items-center gap-1">
                      Usage
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50 font-ui"
                    onClick={() => handleSort('correct_count')}
                  >
                    <div className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-primary mr-1" />
                      Correct
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50 font-ui"
                    onClick={() => handleSort('incorrect_count')}
                  >
                    <div className="flex items-center gap-1">
                      <XCircle className="h-3 w-3 text-destructive mr-1" />
                      Incorrect
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead className="font-ui">
                    <div className="flex items-center gap-1">
                      <HelpCircle className="h-3 w-3 text-muted-foreground mr-1" />
                      Unanswered
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50 font-ui"
                    onClick={() => handleSort('avg_grade')}
                  >
                    <div className="flex items-center gap-1">
                      Avg Grade
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead className="w-[50px] font-ui">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && data.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-muted-foreground font-ui"
                    >
                      <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                      Loading data...
                    </TableCell>
                  </TableRow>
                ) : data.length > 0 ? (
                  data.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium font-ui">
                        <div
                          className="truncate max-w-[300px]"
                          title={item.question_text}
                        >
                          {item.question_text}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="outline" className="text-xs font-ui">
                            {item.level}
                          </Badge>
                          <Badge
                            variant="secondary"
                            className="text-xs capitalize font-ui"
                          >
                            {getModuleDisplayName(item.module)}
                          </Badge>
                          {item.section && (
                            <Badge variant="outline" className="text-xs font-ui">
                              S{item.section}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-ui">
                        {Number.parseInt(item.usage_count, 10).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-primary font-ui">
                            {formatPercentage(item.correct_count, item.usage_count)}
                          </span>
                          <span className="text-xs text-muted-foreground font-ui">
                            {item.correct_count}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-destructive font-ui">
                            {formatPercentage(
                              item.incorrect_count,
                              item.usage_count,
                            )}
                          </span>
                          <span className="text-xs text-muted-foreground font-ui">
                            {item.incorrect_count}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-muted-foreground font-ui">
                            {formatPercentage(
                              item.unanswered_count,
                              item.usage_count,
                            )}
                          </span>
                          <span className="text-xs text-muted-foreground font-ui">
                            {item.unanswered_count}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            Number.parseFloat(item.avg_grade) >= 60
                              ? 'default'
                              : 'secondary'
                          }
                          className={
                            Number.parseFloat(item.avg_grade) >= 60
                              ? 'bg-primary hover:bg-primary/90 font-ui'
                              : 'font-ui'
                          }
                        >
                          {item.avg_grade || '0'}%
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handlePreview(item.id)}
                          disabled={previewLoading}
                        >
                          {previewLoading && previewQuestion?.id === item.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground hover:text-primary" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-muted-foreground font-ui"
                    >
                      No data available for selected filters
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-end space-x-2 py-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="font-ui">Previous</span>
            </Button>
            <div className="text-sm font-medium font-ui">
              Page {page} of {totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <span className="font-ui">Next</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Question Preview Dialog */}
      <Dialog
        open={!!previewQuestion}
        onOpenChange={(open) => !open && setPreviewQuestion(null)}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-ui">Question Preview</DialogTitle>
            <DialogDescription className="font-ui">
              View detailed information about this question
            </DialogDescription>
          </DialogHeader>
          {previewQuestion && (
            <QuestionPreview
              question={previewQuestion}
              onClose={() => setPreviewQuestion(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
